import React from 'react';
import { AnimatedButton } from './AnimatedButton';
import {
  ADDRESS_TEXT,
  GOOGLE_BUSINESS_URL,
  MAP_EMBED_URL,
  MAP_LABEL,
  MAP_LINK_URL,
  PHONE_DISPLAY,
  PHONE_TEL,
  RESERVATION_URL,
} from '../lib/site';

interface HoursSectionProps {
  isPink?: boolean;
  title?: string;
  description?: string;
  subTitle?: string;
  subDescription?: string;
}

const SOFT_PINK = "#FEADBB";

export const HoursSection: React.FC<HoursSectionProps> = ({ 
  isPink = false, 
  title = "Un service expert pour votre style unique.",
  description = "Parce que chaque détail compte, nous prenons le temps d'analyser vos besoins pour vous offrir une coupe et un soin sur mesure. Notre objectif est de sublimer votre apparence dans une ambiance détendue et professionnelle.",
  subTitle = "",
  subDescription = ""
}) => {

  const hours = [
    { day: 'Lundi', time: 'Fermé', isClosed: true },
    { day: 'Mardi', time: '10:00 - 20:00' },
    { day: 'Mercredi', time: '10:00 - 20:00' },
    { day: 'Jeudi', time: '10:00 - 20:00' },
    { day: 'Vendredi', time: '10:00 - 20:00' },
    { day: 'Samedi', time: '10:00 - 20:00' },
    { day: 'Dimanche', time: 'Fermé', isClosed: true },
  ];

  return (
    <section className="relative w-full min-h-screen stable-screen-min-height bg-white text-[#1d1d1d] overflow-hidden flex items-center py-16 md:py-20 px-6 lg:px-12">
      
      {/* Decorative background title/elements */}
      <div
        className={`absolute top-20 left-0 w-full text-center pointer-events-none ${isPink ? 'opacity-[0.07]' : 'opacity-[0.03]'}`}
      >
        <span className={`text-[15vw] font-bold uppercase leading-none ${isPink ? 'text-[#FEADBB]' : 'text-[#1d1d1d]'}`}>
          Contact
        </span>
      </div>

      <div className="relative z-10 w-full max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-12 gap-14 md:gap-16 lg:gap-16 items-start h-full pt-12">
        
        {/* LEFT COLUMN: HOURS CARD */}
        <div className="lg:col-span-5 w-full flex flex-col justify-center h-full lg:sticky lg:top-24">
          <div className={`${isPink ? 'bg-[#FEADBB]' : 'bg-[#1d1d1d]'} rounded-3xl p-8 lg:p-10 shadow-2xl text-white relative overflow-hidden w-full`}>
            {/* Decorative background element for the card header */}
            <div className="absolute top-0 right-0 p-6 opacity-10">
               <svg width="100" height="100" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1">
                 <circle cx="12" cy="12" r="10" />
                 <path d="M12 6v6l4 2" />
               </svg>
            </div>

            <div className="relative z-10 mb-8">
              <span className={`text-xs font-bold uppercase tracking-widest ${isPink ? 'text-white/80' : 'text-gray-300'}`}>Ouverture</span>
              <h3 className={`text-3xl lg:text-4xl font-bold mt-2 tracking-tight ${isPink ? 'text-white' : 'text-white'}`}>Nos Horaires</h3>
            </div>

            <div className="space-y-4 mb-10">
              {hours.map((item) => (
                <div key={item.day} className={`flex justify-between items-center text-sm lg:text-base border-b pb-3 last:border-0 last:pb-0 ${isPink ? 'border-white/20' : 'border-gray-800'}`}>
                  <span className={`font-medium ${isPink ? 'text-white' : 'text-gray-400'}`}>{item.day}</span>
                  <span className={`font-bold ${item.isClosed ? (isPink ? 'text-white/70 italic' : 'text-gray-300 italic') : (isPink ? 'text-white' : 'text-white')}`}>
                    {item.time}
                  </span>
                </div>
              ))}
            </div>

            <div className="space-y-4">
              <AnimatedButton
                href="#contact"
                className={`w-full justify-start ${isPink ? 'bg-white text-[#FEADBB] border-white hover:bg-white/90' : ''}`}
                {...(!isPink ? { dark: true } : {})}
              >
                Nous contacter
              </AnimatedButton>
            </div>
          </div>
        </div>

        {/* RIGHT COLUMN: MAP (TOP) + TEXT CONTENT (BOTTOM) */}
        <div className="lg:col-span-7 flex flex-col gap-10 md:gap-14 lg:gap-16 pt-1 md:pt-2 lg:pt-0">
          
          {/* MAP */}
          <div id="contact" className={`w-full mt-2 md:mt-4 lg:mt-0 h-[360px] md:h-[420px] lg:h-[500px] rounded-3xl overflow-hidden shadow-2xl border relative group ${isPink ? 'border-pink-50' : 'border-gray-200'}`}>
            <iframe 
              width="100%" 
              height="100%" 
              frameBorder="0" 
              loading="lazy"
              style={{ border: 0 }}
              src={MAP_EMBED_URL}
              allowFullScreen
              title="Google Maps"
              className="transition-opacity duration-700"
            ></iframe>
            
            <div className="absolute bottom-4 md:bottom-6 left-4 md:left-6 bg-white/90 backdrop-blur-md px-5 md:px-6 py-3.5 md:py-4 rounded-2xl border border-black/5 pointer-events-auto shadow-lg max-w-[90%] md:max-w-[82%]">
               <h3 className="text-[#1d1d1d] font-bold text-lg">Notre salon</h3>
               <div className="mt-1.5 flex flex-col gap-3 text-sm">
                 <div className="flex flex-col">
                   <span className="text-[10px] uppercase tracking-[0.22em] text-gray-500 mb-0.5">
                     Adresse du salon
                   </span>
                   <a
                     href={MAP_LINK_URL}
                     target="_blank"
                     rel="noopener noreferrer"
                     className={`transition-colors font-medium ${isPink ? 'text-gray-700 hover:text-[#FEADBB]' : 'text-gray-700 hover:text-[#1d1d1d]'}`}
                   >
                     {ADDRESS_TEXT}
                   </a>
                   <a
                     href={MAP_LINK_URL}
                     target="_blank"
                     rel="noopener noreferrer"
                     className={`text-xs transition-colors underline underline-offset-2 ${isPink ? 'text-[#FEADBB] hover:opacity-70' : 'text-gray-600 hover:text-[#1d1d1d]'}`}
                   >
                     {MAP_LABEL}
                   </a>
                 </div>

                 <div className="flex flex-col">
                   <span className="text-[10px] uppercase tracking-[0.22em] text-gray-500 mb-0.5">
                     Téléphone du salon
                   </span>
                   <a
                     href={`tel:${PHONE_TEL}`}
                     className={`transition-colors font-semibold ${isPink ? 'text-[#FEADBB]' : 'text-gray-700 hover:text-[#1d1d1d]'}`}
                   >
                     {PHONE_DISPLAY}
                   </a>
                 </div>
               </div>
            </div>
          </div>

          {/* CONTENT */}
          <div className="flex flex-col space-y-6">
            <div className={`text-sm font-bold uppercase tracking-widest ${isPink ? 'text-[#FEADBB]' : 'text-gray-600'}`}>( Votre Rendez-vous )</div>
            <h2 className="text-4xl lg:text-6xl font-medium tracking-tight leading-[1.1]">
              {title}
            </h2>
            <p className="text-lg lg:text-xl text-gray-600 leading-relaxed max-w-2xl">
              {description}
            </p>

            <div className="pt-4">
              <p className="text-xs font-bold uppercase tracking-widest text-gray-500 mb-2">Téléphone du salon</p>
              <a
                href={`tel:${PHONE_TEL}`}
                className={`inline-flex text-lg lg:text-xl font-semibold transition-all ${isPink ? 'text-[#FEADBB] hover:opacity-70' : 'text-[#1d1d1d] hover:opacity-70'}`}
              >
                {PHONE_DISPLAY}
              </a>
              <div className="mt-5">
                <AnimatedButton
                  href={RESERVATION_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={isPink ? 'bg-[#FEADBB] border-[#FEADBB] text-white hover:bg-white hover:text-[#FEADBB]' : ''}
                  {...(!isPink ? { dark: true } : {})}
                >
                  Réserver maintenant
                </AnimatedButton>
              </div>
            </div>
            
            {subTitle && (
              <div className={`pt-4 border-t mt-4 ${isPink ? 'border-pink-50' : 'border-gray-200'}`}>
                <h3 className="font-bold text-xl mb-2">{subTitle}</h3>
                <p className="text-gray-600">{subDescription}</p>

              <div className="mt-6 flex flex-col items-start gap-4">
                <AnimatedButton
                  href={RESERVATION_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={isPink ? 'bg-[#FEADBB] border-[#FEADBB] text-white hover:bg-white hover:text-[#FEADBB]' : ''}
                  {...(!isPink ? { dark: true } : {})}
                >
                  Réserver sur Planity
                </AnimatedButton>

                <a
                  href={GOOGLE_BUSINESS_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={`inline-flex text-sm font-semibold underline underline-offset-2 transition-colors ${isPink ? 'text-[#FEADBB] hover:opacity-70' : 'text-gray-700 hover:text-[#1d1d1d]'}`}
                >
                  Voir Google Business
                </a>
              </div>
            </div>
            )}

          </div>

        </div>

      </div>
    </section>
  );
};
